import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Tag, StickyNote, Plus, X, Save, Loader2 } from 'lucide-react';

const PRESET_TAGS = [
  'Urgente', 'Revisión Pendiente', 'Firmado', 'En Negociación',
  'Vencido', 'Renovar', 'Archivado', 'Importante'
];

const ContractNotesAndTags = ({ contractId }) => {
  const { api } = useAuth();
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotesAndTags();
  }, [contractId]);

  const fetchNotesAndTags = async () => {
    try {
      const response = await api.get(`/contracts/${contractId}/notes`);
      setNotes(response.data.notes || '');
      setTags(response.data.tags || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.post(`/contracts/${contractId}/notes`, { notes });
      toast.success('Notas guardadas');
    } catch (error) {
      toast.error('Error al guardar notas');
    } finally {
      setSaving(false);
    }
  };

  const saveTags = async (updatedTags) => {
    try {
      await api.put(`/contracts/${contractId}/tags`, { tags: updatedTags });
    } catch (error) {
      toast.error('Error al guardar etiquetas');
    }
  };

  const addTag = (tag) => {
    const trimmed = tag.trim();
    if (!trimmed || tags.includes(trimmed) || tags.length >= 10) return;
    const updated = [...tags, trimmed];
    setTags(updated);
    saveTags(updated);
    setNewTag('');
  };

  const removeTag = (tag) => {
    const updated = tags.filter(t => t !== tag);
    setTags(updated);
    saveTags(updated);
  };

  if (loading) {
    return <div className="animate-pulse h-32 bg-slate-800/50 rounded-xl" />;
  }

  return (
    <div className="space-y-4">
      {/* Tags Section */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-4">
          <Tag className="w-4 h-4 text-purple-400" />
          Etiquetas
        </h3>
        
        {/* Current Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          {tags.map(tag => (
            <span
              key={tag}
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-500/20 border border-purple-500/30 text-purple-300 rounded-full text-xs"
            >
              {tag}
              <button onClick={() => removeTag(tag)} className="hover:text-red-400 transition-colors">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {tags.length === 0 && (
            <span className="text-xs text-slate-500">Sin etiquetas</span>
          )}
        </div>

        {/* Preset Tags */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {PRESET_TAGS.filter(t => !tags.includes(t)).slice(0, 6).map(tag => (
            <button
              key={tag}
              onClick={() => addTag(tag)}
              className="px-2.5 py-1 border border-dashed border-slate-600 text-slate-400 rounded-full text-xs hover:border-purple-500/50 hover:text-purple-400 transition-colors"
            >
              + {tag}
            </button>
          ))}
        </div>

        {/* Custom Tag Input */}
        <div className="flex gap-2">
          <input
            type="text"
            value={newTag}
            onChange={e => setNewTag(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addTag(newTag)}
            placeholder="Nueva etiqueta..."
            maxLength={30}
            className="flex-1 bg-slate-800 border border-border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-purple-500/50 text-white placeholder-slate-500"
          />
          <button
            onClick={() => addTag(newTag)}
            disabled={!newTag.trim()}
            className="p-1.5 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Notes Section */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-4">
          <StickyNote className="w-4 h-4 text-amber-400" />
          Notas Personales
        </h3>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Agrega notas privadas sobre este contrato..."
          rows={4}
          className="w-full bg-slate-800/50 border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500/50 text-white placeholder-slate-500 resize-none"
        />
        <div className="flex justify-end mt-2">
          <button
            onClick={saveNotes}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg text-sm hover:bg-amber-500/30 transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractNotesAndTags;
